import { Link } from '@tanstack/react-router'
import type { LucideIcon } from 'lucide-react'
import type { ReactNode } from 'react'
import { cn } from '@/lib/utils'

export interface SettingsSidebarItem<T extends string = string> {
  title: string
  value: T
  icon: LucideIcon
  badge?: ReactNode
}

export interface SettingsSidebarGroup<T extends string = string> {
  label: string
  items: SettingsSidebarItem<T>[]
}

interface SettingsSidebarBaseProps<T extends string> {
  items: SettingsSidebarItem<T>[]
  currentSection: T
  className?: string
}

interface SettingsSidebarButtonProps<T extends string> extends SettingsSidebarBaseProps<T> {
  mode: 'button'
  onSectionChange: (section: T) => void
}

interface SettingsSidebarLinkProps<T extends string> extends SettingsSidebarBaseProps<T> {
  mode: 'link'
  routePath: string
}

type SettingsSidebarProps<T extends string> = SettingsSidebarButtonProps<T> | SettingsSidebarLinkProps<T>

/**
 * Generic settings sidebar component that supports both button and link modes
 *
 * @example
 * // Button mode (for dialogs)
 * <SettingsSidebar
 *   mode="button"
 *   items={items}
 *   currentSection={currentSection}
 *   onSectionChange={setCurrentSection}
 * />
 *
 * @example
 * // Link mode (for pages with URL-based navigation)
 * <SettingsSidebar
 *   mode="link"
 *   items={items}
 *   currentSection={currentSection}
 *   routePath="/organization"
 * />
 */
export function SettingsSidebar<T extends string>(props: SettingsSidebarProps<T>) {
  const { items, currentSection, className } = props

  return (
    <aside className={cn('flex flex-col bg-background', className)}>
      <nav className="flex flex-col gap-1 p-4">
        {items.map((item) => {
          const isActive = currentSection === item.value
          const Icon = item.icon

          const buttonClasses = cn(
            'flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
            isActive
              ? 'bg-secondary text-secondary-foreground'
              : 'text-muted-foreground hover:bg-secondary/50 hover:text-foreground'
          )

          if (props.mode === 'button') {
            return (
              <button
                key={item.value}
                onClick={() => props.onSectionChange(item.value)}
                className={buttonClasses}
              >
                <Icon className="h-4 w-4" />
                <span>{item.title}</span>
              </button>
            )
          }

          return (
            <Link
              key={item.value}
              to={props.routePath}
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              search={{ section: item.value as any }}
              className={cn(buttonClasses, 'justify-between')}
            >
              <div className="flex items-center gap-3">
                <Icon className="h-4 w-4" />
                <span>{item.title}</span>
              </div>
              {typeof item.badge === 'number'
                ? item.badge > 0 && (
                    <span className="flex h-5 min-w-[20px] items-center justify-center rounded-full bg-red-100 px-1.5 text-xs font-semibold text-red-700 dark:bg-red-900/30 dark:text-red-400">
                      {item.badge > 99 ? '99+' : item.badge}
                    </span>
                  )
                : item.badge}
            </Link>
          )
        })}
      </nav>
    </aside>
  )
}

interface GroupedSettingsSidebarProps<T extends string> {
  groups: SettingsSidebarGroup<T>[]
  currentSection: T
  routePath: string
  className?: string
}

export function GroupedSettingsSidebar<T extends string>(props: GroupedSettingsSidebarProps<T>) {
  const { groups, currentSection, routePath, className } = props

  return (
    <aside className={cn('flex flex-col bg-background', className)}>
      <nav className="flex flex-col gap-1 p-4">
        {groups.map((group, groupIndex) => (
          <div key={group.label} className={cn(groupIndex > 0 && 'mt-4')}>
            <div className="px-3 py-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              {group.label}
            </div>
            {group.items.map((item) => {
              const isActive = currentSection === item.value
              const Icon = item.icon

              return (
                <Link
                  key={item.value}
                  to={routePath}
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  search={{ section: item.value as any }}
                  className={cn(
                    'flex items-center justify-between rounded-lg px-3 py-2 text-sm font-medium transition-colors',
                    isActive
                      ? 'bg-secondary text-secondary-foreground'
                      : 'text-muted-foreground hover:bg-secondary/50 hover:text-foreground'
                  )}
                >
                  <div className="flex items-center gap-3">
                    <Icon className="h-4 w-4" />
                    <span>{item.title}</span>
                  </div>
                  {typeof item.badge === 'number'
                    ? item.badge > 0 && (
                        <span className="flex h-5 min-w-[20px] items-center justify-center rounded-full bg-red-100 px-1.5 text-xs font-semibold text-red-700 dark:bg-red-900/30 dark:text-red-400">
                          {item.badge > 99 ? '99+' : item.badge}
                        </span>
                      )
                    : item.badge}
                </Link>
              )
            })}
          </div>
        ))}
      </nav>
    </aside>
  )
}
